import React, { useState, useEffect } from 'react';
import { Button } from './button';
import { MessageCircle } from 'lucide-react';
import api from '../../lib/api';

/**
 * Formats a phone number for WhatsApp (removes spaces, dashes, and adds country code if needed)
 */
const formatPhoneForWhatsApp = (phone) => {
  if (!phone) return null;
  // Remove all non-numeric characters except +
  let cleaned = phone.replace(/[^\d+]/g, '');
  // If number doesn't start with +, assume it's Indian and add +91
  if (!cleaned.startsWith('+')) {
    if (cleaned.startsWith('91') && cleaned.length === 12) {
      cleaned = '+' + cleaned;
    } else if (cleaned.length === 10) {
      cleaned = '+91' + cleaned;
    }
  }
  return cleaned.replace('+', '');
};

/**
 * Generates a WhatsApp URL with prefilled message
 */
const generateWhatsAppUrl = (phone, message) => {
  const formattedPhone = formatPhoneForWhatsApp(phone);
  if (!formattedPhone) return null;
  const encodedMessage = encodeURIComponent(message);
  return `https://wa.me/${formattedPhone}?text=${encodedMessage}`;
};

/**
 * Replaces template variables with actual values
 * Template format: {Variable_Name}
 */
const replaceTemplateVariables = (template, data) => {
  if (!template) return '';
  
  return template.replace(/\{([^}]+)\}/g, (match, variable) => {
    const value = data[variable];
    if (value === undefined || value === null || value === '') {
      return '-';
    }
    return value;
  });
};

/**
 * Opens WhatsApp with the given URL
 * Uses different approaches for mobile vs desktop
 */
const openWhatsApp = (url) => {
  if (!url) return;
  
  // Detect if mobile
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  
  if (isMobile) {
    // On mobile, use location.href for better compatibility
    window.location.href = url;
  } else {
    // On desktop, open in new tab
    window.open(url, '_blank');
  }
};

/**
 * Generic WhatsApp button component
 */
const WhatsAppButton = ({ 
  phone, 
  message, 
  label = "WhatsApp",
  className = "",
  disabled = false
}) => {
  const handleClick = () => {
    if (!phone) {
      alert('Phone number not available.');
      return;
    }
    const url = generateWhatsAppUrl(phone, message);
    openWhatsApp(url);
  };

  return (
    <Button 
      type="button"
      variant="outline"
      size="sm"
      onClick={handleClick}
      disabled={disabled || !phone}
      className={`gap-1.5 ${className}`}
      title={phone ? `Send WhatsApp to ${phone}` : 'No phone number available'}
    >
      <MessageCircle className="h-4 w-4 text-green-600" />
      <span>{label}</span>
    </Button>
  );
};

/**
 * OrderWhatsAppButton - Loads template dynamically from settings
 */
const OrderWhatsAppButton = ({ order, supplierPhone }) => {
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    if (!supplierPhone) {
      alert('Supplier phone number not available. Please add phone number in Supplier master.');
      return;
    }

    setLoading(true);
    try {
      // Fetch template from settings API
      const response = await api.get('/settings/whatsapp-template/order_confirmation');
      const template = response.data;
      
      // Prepare data for variable replacement
      const data = {
        Order_ID: order.order_number,
        Order_Date: order.order_date,
        Supplier_Name: order.supplier_name,
        City: order.city,
        Item: order.item || order.item_name || '-',
        Quality: order.quality_name,
        Finish_Quality: order.subquality_name || '-',
        Quantity: order.ordered_quantity,
        Unit: order.order_unit,
        Rate: order.rate,
        Total: order.order_amount?.toLocaleString() || '-',
        Remarks: order.remarks ? `Remarks: ${order.remarks}` : ''
      };
      
      // Replace variables in template
      const message = replaceTemplateVariables(template.message_template, data);
      
      // Open WhatsApp
      const url = generateWhatsAppUrl(supplierPhone, message);
      openWhatsApp(url);
    } catch (error) {
      console.error('Failed to load WhatsApp template:', error);
      // Fallback to default message
      const fallbackMessage = `*Order Confirmation*\n\nOrder: ${order.order_number}\nSupplier: ${order.supplier_name}\nItem: ${order.item || '-'}\nQuantity: ${order.ordered_quantity} ${order.order_unit}\n\n- Jai Matadi Grey Order Management`;
      const url = generateWhatsAppUrl(supplierPhone, fallbackMessage);
      openWhatsApp(url);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button 
      type="button"
      variant="outline"
      size="sm"
      onClick={handleClick}
      disabled={loading}
      className="gap-1.5 text-green-700 hover:text-green-800 hover:bg-green-50"
      title={supplierPhone ? `Send WhatsApp to ${supplierPhone}` : 'No supplier phone available'}
    >
      <MessageCircle className="h-4 w-4 text-green-600" />
      <span>{loading ? 'Loading...' : 'Send Order'}</span>
    </Button>
  );
};

/**
 * DriverWhatsAppButton - Pre-configured button for driver notification
 */
const DriverWhatsAppButton = ({ driver, lrNumbers, vehicleNumber }) => {
  if (!driver?.mobile_number && !driver?.driver_mobile) return null;
  
  const phone = driver.mobile_number || driver.driver_mobile;
  const message = `*LR Delivery Dispatch*\n\nDriver: ${driver.driver_name}\nVehicle: ${vehicleNumber || driver.vehicle_number || '-'}\n\nLRs: ${(lrNumbers || []).join(', ')}\n\n- Jai Matadi Grey Order Management`;
  
  return (
    <WhatsAppButton 
      phone={phone}
      message={message}
      label="WhatsApp Driver"
      className="text-green-700 hover:text-green-800 hover:bg-green-50"
    />
  );
};

/**
 * LRIssueWhatsAppButton - Loads template dynamically from settings
 */
const LRIssueWhatsAppButton = ({ issue, lrNumbers = [] }) => {
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    if (!issue?.driver_mobile) {
      alert('Driver phone number not available for this delivery issue.');
      return;
    }

    setLoading(true);
    try {
      // Fetch template from settings API
      const response = await api.get('/settings/whatsapp-template/lr_issue');
      const template = response.data;
      
      // Prepare data for variable replacement
      const data = {
        Issue_ID: issue.issue_number || issue.lr_delivery_issue_id || '-',
        Issue_Date: issue.issue_date || '-',
        Driver_Name: issue.driver_name || '-',
        Driver_Mobile: issue.driver_mobile || '-',
        Vehicle_Number: issue.vehicle_number || '-',
        Gate_Pass: issue.gate_pass_no || '-',
        LR_Numbers: lrNumbers.length > 0 ? lrNumbers.join(', ') : (issue.items?.map(i => i.lr_number).join(', ') || '-'),
        Remarks: issue.remarks ? `Remarks: ${issue.remarks}` : ''
      };
      
      // Replace variables in template
      const message = replaceTemplateVariables(template.message_template, data);
      
      // Open WhatsApp
      const url = generateWhatsAppUrl(issue.driver_mobile, message);
      openWhatsApp(url);
    } catch (error) {
      console.error('Failed to load WhatsApp template:', error);
      // Fallback message
      const fallbackMessage = `*LR Delivery Issue*\n\nDriver: ${issue.driver_name || '-'}\nVehicle: ${issue.vehicle_number || '-'}\nGate Pass: ${issue.gate_pass_no || '-'}\n\n- Jai Matadi Grey Order Management`;
      const url = generateWhatsAppUrl(issue.driver_mobile, fallbackMessage);
      openWhatsApp(url);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button 
      type="button"
      variant="outline"
      size="sm"
      onClick={handleClick}
      disabled={loading}
      className="gap-1.5 text-green-700 hover:text-green-800 hover:bg-green-50"
      title={issue?.driver_mobile ? `Send WhatsApp to ${issue.driver_mobile}` : 'No driver phone available'}
    >
      <MessageCircle className="h-4 w-4 text-green-600" />
      <span>{loading ? 'Loading...' : 'WhatsApp'}</span>
    </Button>
  );
};

export { 
  WhatsAppButton, 
  OrderWhatsAppButton, 
  DriverWhatsAppButton,
  LRIssueWhatsAppButton,
  generateWhatsAppUrl,
  formatPhoneForWhatsApp,
  replaceTemplateVariables
};
